import { useMemo, useState } from "react";
import { ArrowLeft, Plus, Users } from "lucide-react";
import { Link, useParams } from "react-router";

import { UserAvatar } from "@/components/UserAvatar";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardDialog } from "@/features/board/CardDialog";
import { ColumnDialog } from "@/features/board/ColumnDialog";
import { KanbanBoard } from "@/features/board/KanbanBoard";
import { MemberDialog } from "@/features/board/MemberDialog";
import type {
  AppUser,
  BoardCard,
  BoardColumn,
  CardInput,
  ProjectColor,
} from "@/features/board/types";
import { useBoard } from "@/features/board/useBoard";

type BoardPageProps = {
  user: AppUser;
};

const colorClasses: Record<ProjectColor, string> = {
  rose: "bg-primary",
  violet: "bg-violet-500",
  blue: "bg-blue-500",
  teal: "bg-teal-500",
  amber: "bg-amber-500",
};

export const BoardPage = ({ user }: BoardPageProps) => {
  const { projectId = "" } = useParams();
  const boardState = useBoard(projectId);
  const [cardState, setCardState] = useState<{
    card: BoardCard | null;
    columnId: string;
  } | null>(null);
  const [memberOpen, setMemberOpen] = useState(false);
  const [columnState, setColumnState] = useState<BoardColumn | "new" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const cardCount = useMemo(
    () => boardState.board?.columns.reduce((total, column) => total + column.cards.length, 0) ?? 0,
    [boardState.board?.columns]
  );

  if (boardState.loading) {
    return (
      <section className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="h-8 w-56 animate-pulse rounded bg-neutral-200" />
        <div className="flex gap-3 pt-8 overflow-hidden">
          {[0, 1, 2, 3].map((item) => (
            <div className="h-[32rem] w-72 shrink-0 animate-pulse rounded-xl bg-neutral-200/70 sm:w-80" key={item} />
          ))}
        </div>
      </section>
    );
  }

  if (!boardState.board) {
    return (
      <section className="py-16">
        <div className="mx-auto max-w-xl px-4 text-center sm:px-6">
          <h1 className="text-balance text-2xl font-semibold tracking-tight text-neutral-950">
            Board unavailable
          </h1>
          <p className="text-pretty pt-2 text-base text-neutral-600 sm:text-sm">
            {boardState.error ?? "This project does not exist or you no longer have access."}
          </p>
          <Button className="mt-5" onClick={() => void boardState.load()} type="button" variant="secondary">
            Try again
          </Button>
        </div>
      </section>
    );
  }

  const { project, members, columns } = boardState.board;
  const owner = project.role === "owner";

  const openNewCard = (columnId?: string) => {
    const targetColumn = columnId ?? columns[0]?.id;
    if (targetColumn) setCardState({ card: null, columnId: targetColumn });
  };

  const saveCard = async (input: CardInput) => {
    if (cardState?.card) {
      const { columnId: _columnId, ...changes } = input;
      await boardState.updateCard(cardState.card.id, changes);
    } else {
      await boardState.createCard(input);
    }
  };

  return (
    <section className="py-6 sm:py-8">
      <div className="px-4 sm:px-6 lg:px-8">
        <Link
          className="inline-flex items-center gap-2 rounded-md text-base text-neutral-600 hover:text-neutral-950 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:text-sm"
          to="/projects"
        >
          <ArrowLeft className="size-4 shrink-0 stroke-current" />
          All projects
        </Link>

        <header className="flex flex-col items-start justify-between gap-5 pt-5 xl:flex-row xl:items-end">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <span aria-hidden="true" className={`size-3 shrink-0 rounded-full ${colorClasses[project.color]}`} />
              <Badge variant="secondary">{owner ? "Owner" : "Member"}</Badge>
            </div>
            <h1 className="text-balance max-w-[32ch] pt-3 text-3xl font-semibold tracking-tight text-neutral-950 sm:text-4xl">
              {project.name}
            </h1>
            <p className="text-pretty max-w-[72ch] pt-2 text-base text-neutral-600 sm:text-sm">
              {project.description || "Move cards across the board as the work advances."}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              aria-label="View project members"
              className="flex h-9 items-center rounded-md px-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              onClick={() => setMemberOpen(true)}
              type="button"
            >
              <span className="flex -space-x-2">
                {members.slice(0, 3).map((member) => (
                  <UserAvatar
                    className="size-7 ring-2 ring-background"
                    image={member.image}
                    key={member.id}
                    name={member.name}
                  />
                ))}
              </span>
              {members.length > 3 ? (
                <span className="pl-2 text-sm tabular-nums text-neutral-500">+{members.length - 3}</span>
              ) : null}
            </button>
            <Button className="pl-2 pr-3" onClick={() => setMemberOpen(true)} type="button" variant="secondary">
              <Users className="size-4 shrink-0 stroke-current" />
              {members.length} {members.length === 1 ? "member" : "members"}
            </Button>
            <Button className="pl-2 pr-3" onClick={() => openNewCard()} type="button">
              <Plus className="size-4 shrink-0 stroke-current" />
              Add card
            </Button>
          </div>
        </header>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-5 text-base text-neutral-500 sm:text-sm">
          <p className="tabular-nums">{cardCount} {cardCount === 1 ? "card" : "cards"}</p>
          <p>Drag cards to reorder or move them between columns.</p>
          <p className="sr-only">Signed in as @{user.username}.</p>
        </div>
      </div>

      {actionError ? (
        <Alert className="mx-4 mt-5 border-0 bg-red-50 text-red-800 ring-1 ring-red-900/10 sm:mx-6 lg:mx-8">
          {actionError}
        </Alert>
      ) : null}

      <div className="pt-6">
        <KanbanBoard
          columns={columns}
          members={members}
          onAddCard={openNewCard}
          onAddColumn={() => setColumnState("new")}
          onDeleteColumn={(column) => {
            setActionError(null);
            void boardState.deleteColumn(column.id).catch((cause) => {
              setActionError(cause instanceof Error ? cause.message : "The column could not be deleted.");
            });
          }}
          onOpenCard={(card) => setCardState({ card, columnId: card.columnId })}
          onRenameColumn={setColumnState}
          onReorder={boardState.reorderCards}
        />
      </div>

      <CardDialog
        card={cardState?.card ?? null}
        columnId={cardState?.columnId ?? columns[0]?.id ?? ""}
        members={members}
        onClose={() => setCardState(null)}
        onDelete={boardState.deleteCard}
        onSave={saveCard}
        open={Boolean(cardState)}
      />
      <MemberDialog
        members={members}
        onAdd={boardState.addMember}
        onClose={() => setMemberOpen(false)}
        onRemove={boardState.removeMember}
        open={memberOpen}
        owner={owner}
      />
      <ColumnDialog
        initialName={columnState && columnState !== "new" ? columnState.name : undefined}
        onClose={() => setColumnState(null)}
        onSave={(name) =>
          columnState && columnState !== "new"
            ? boardState.renameColumn(columnState.id, name)
            : boardState.createColumn(name).then(() => undefined)
        }
        open={Boolean(columnState)}
      />
    </section>
  );
};
